import { VariableNameFinder } from './formulas/variable-name-finder.ts';
import type { ReleaseProfileFormula, ReleaseProfileVariableDefinition } from '../types/release-profiles.ts';
import {
  formulaPredefinedVariables,
  getAllReferencedVarNames,
  notApplicableVar,
  productUsageLbVar,
  productSolidsPercentOfWeightVar,
  productSolidsPercentOfVolumeVar,
  rainfallVar,
} from './formulas/formula-utils.ts';
import type { FormRLeafLine } from './form-r/form-r-sections.ts';
import type { TRIQuantityBasisEstimationCodeType } from '../types/form-r.ts';

// The same allowlist drives authoring and server validation. Chemical concentrations,
// chemical mixes and waste streams do not describe whole-product inventory amounts.
const inventoryVariableNames = new Set([
  productUsageLbVar,
  productSolidsPercentOfWeightVar,
  productSolidsPercentOfVolumeVar,
  rainfallVar,
  notApplicableVar,
]);
export const inventoryPredefinedVariables = formulaPredefinedVariables
  .filter((variable) => inventoryVariableNames.has(variable.nameForVariable))
  .map((variable) =>
    variable.nameForVariable === notApplicableVar
      ? { ...variable, description: 'This inventory calculation is not applicable.' }
      : variable,
  );

export function isInventoryVariable(definition: ReleaseProfileVariableDefinition): boolean {
  return definition.type === 'constant' || (definition.type === 'question' && !definition.isChemicalSpecific);
}

export function releaseProfileValidateForInventory(
  formula: ReleaseProfileFormula,
  variables: readonly { nameForVariable: string; definition: ReleaseProfileVariableDefinition }[],
  formRLine: FormRLeafLine | '',
  basis: TRIQuantityBasisEstimationCodeType | '',
): void {
  if (formRLine || basis) {
    throw new Error('Inventory profiles cannot have a Form R line or release basis.');
  }
  const allowed = new Set([
    ...inventoryVariableNames,
    ...variables
      .filter((variable) => isInventoryVariable(variable.definition))
      .map((variable) => variable.nameForVariable),
    ...Object.values(formula.formulas).map((item) => item.nameForVariable),
  ]);
  for (const item of Object.values(formula.formulas)) {
    for (const output of Object.values(item.outputs)) {
      const isInventoryOutput = output.type === 'tier2Max' || output.type === 'tier2Avg';
      if (!isInventoryOutput) throw new Error('Inventory amounts and release outputs must use separate profiles.');
      // An inventory amount describes a whole product rather than a chemical within it, so it declares the same
      // 'noChemicals' scope a compliance metric's throughput does. That is what makes the shared calculation engine
      // evaluate the formula once rather than once per chemical in the product.
      if (output.chemicals !== 'noChemicals' || output.basis) {
        throw new Error('Inventory outputs cannot select chemicals or specify a release basis.');
      }
    }
    const parsed = VariableNameFinder.findIdentifiersInString(item.formula);
    if (parsed.status === 'success' && parsed.identifiers.some((identifier) => identifier.annotation)) {
      throw new Error('Inventory formulas cannot use chemical annotations.');
    }
    for (const name of getAllReferencedVarNames(item.formula, formula.formulas)) {
      if (!allowed.has(name))
        throw new Error(
          `Variable "${name}" is not supported in inventory profiles. Use constants or non-chemical questions.`,
        );
    }
  }
}
